import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import cities from '../data/cities';

function offsetLabel(timezone, homeTimezone, atDate) {
  const here = atDate.setZone(timezone).offset;
  const home = atDate.setZone(homeTimezone).offset;
  const diff = (here - home) / 60;
  if (diff === 0) return '±0';
  const rounded = Math.round(diff * 10) / 10;
  return rounded > 0 ? `+${rounded}` : `−${Math.abs(rounded)}`;
}

function localizedCityName(city, lang) {
  if (!city) return '';
  if (lang?.startsWith('fr') && city.nameFr) return city.nameFr;
  if (lang?.startsWith('es') && city.nameEs) return city.nameEs;
  if (lang?.startsWith('de') && city.nameDe) return city.nameDe;
  if (lang?.startsWith('pt') && city.namePt) return city.namePt;
  if (lang?.startsWith('zh') && city.nameZh) return city.nameZh;
  return city.name;
}

// Builds the city-row data, applies the user's sort preference (auto:
// home first then closest-offset; manual: their saved order), and exposes
// the drag handlers used while reordering.
export function useCityRowOrdering({ selectedTimezones, homeCity, HOME_TZ, onReorderCities, language }) {
  const cityRows = useMemo(() => {
    const list = (selectedTimezones && selectedTimezones.length > 0)
      ? selectedTimezones
      : (homeCity ? [homeCity] : []);
    const ref = DateTime.now();
    return list.map(tz => {
      const city = cities.find(c => c.name === tz.name) || tz;
      const offsetHours = (ref.setZone(city.timezone).offset - ref.setZone(HOME_TZ).offset) / 60;
      return {
        name: city.name,
        displayName: localizedCityName(city, language),
        timezone: city.timezone,
        tzAbbr: city.timezoneName || '',
        offsetLabel: offsetLabel(city.timezone, HOME_TZ, ref),
        offsetHours,
        isHome: city.name === homeCity?.name,
      };
    });
  }, [selectedTimezones, homeCity, HOME_TZ, language]);

  const [sortMode, setSortMode] = useState(() => localStorage.getItem('v2SortMode') || 'auto');
  useEffect(() => { localStorage.setItem('v2SortMode', sortMode); }, [sortMode]);
  const [reorderActive, setReorderActive] = useState(false);
  const [draggingName, setDraggingName] = useState(null);

  const orderedCityRows = useMemo(() => {
    const home = cityRows.find(c => c.isHome);
    if (sortMode === 'manual') {
      // Honour the user's chosen order, but home stays pinned at the top.
      const rest = cityRows.filter(c => !c.isHome);
      return home ? [home, ...rest] : rest;
    }
    const rest = cityRows
      .filter(c => !c.isHome)
      .sort((a, b) => {
        const da = Math.abs(a.offsetHours);
        const db = Math.abs(b.offsetHours);
        if (da !== db) return da - db;
        if (a.offsetHours !== b.offsetHours) return b.offsetHours - a.offsetHours;
        return a.name.localeCompare(b.name);
      });
    return home ? [home, ...rest] : rest;
  }, [cityRows, sortMode]);

  const handleRowDragStart = (name) => () => setDraggingName(name);
  const handleRowDragOver = (name) => (e) => {
    e.preventDefault();
    if (!draggingName || draggingName === name) return;
    const movable = orderedCityRows.filter(c => !c.isHome).map(c => c.name);
    const fromIdx = movable.indexOf(draggingName);
    const toIdx = movable.indexOf(name);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const next = [...movable];
    const [m] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, m);
    const home = orderedCityRows.find(c => c.isHome);
    const finalNames = home ? [home.name, ...next] : next;
    onReorderCities && onReorderCities(finalNames);
  };
  const handleRowDragEnd = () => setDraggingName(null);

  const startReorder = () => {
    setSortMode('manual');
    setReorderActive(true);
  };
  const finishReorder = () => setReorderActive(false);

  return {
    orderedCityRows,
    reorderActive,
    startReorder,
    finishReorder,
    handleRowDragStart,
    handleRowDragOver,
    handleRowDragEnd,
  };
}
